import React from 'react';
import { Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { ReportData } from '../../types/report.types';
import { useSectionPages } from '../SectionPageContext';
import { renderPdfElements, PdfElement, imageProcessor } from '../pdfHtmlImageUtils';

interface ScopePageProps {
  reportData: ReportData;
  sectionId?: string;
}

const styles = StyleSheet.create({
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 0,
    right: 0,
    fontSize: 10,
    color: 'grey',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  footerLeft: {
    textAlign: 'left',
    flex: 1,
  },
  footerRight: {
    textAlign: 'right',
    flex: 1,
  },
});

function sanitizeHtml(html: string): string {
  return html.replace(/(on\w+|javascript:|data-(?!language))="[^"]*"/gi, '');
}

function ensurePdfElements(field: any): PdfElement[] {
  if (!field) return [];
  if (Array.isArray(field) && field.length > 0 && typeof field[0] === 'object' && 'type' in field[0]) {
    return field;
  }
  if (typeof field === 'string') {
    if (field.trim().startsWith('<') || field.includes('<')) {
      const cleanHtml = sanitizeHtml(field);
      return parseHtmlToElementsSync(cleanHtml);
    }
    return [{ type: 'text', content: field }];
  }
  return [{ type: 'text', content: String(field) }];
}

function parseHtmlToElementsSync(htmlString: string): PdfElement[] {
  if (!htmlString) return [];
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return [{ type: 'text', content: htmlString.replace(/<[^>]+>/g, '') }];
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const elements: PdfElement[] = [];

  const processNode = (node: Node, container: PdfElement[] = elements, activeStyles = ''): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      const raw = node.textContent ?? '';
      const normalized = raw.replace(/\s+/g, ' ');
      if (normalized.trim().length > 0) {
        container.push({ type: 'text', content: normalized, style: activeStyles || undefined });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      let newStyles = activeStyles;
      const styleAttr = el.getAttribute('style') || '';
      if (styleAttr) {
        if (/text-decoration(-line)?:\s*underline/i.test(styleAttr)) {
          newStyles = (newStyles ? newStyles + ' ' : '') + 'underline';
        }
        if (/text-decoration(-line)?:\s*line-through/i.test(styleAttr)) {
          newStyles = (newStyles ? newStyles + ' ' : '') + 'strikethrough';
        }
        if (/font-weight:\s*(bold|700)/i.test(styleAttr)) {
          newStyles = (newStyles ? newStyles + ' ' : '') + 'bold';
        }
        if (/font-style:\s*italic/i.test(styleAttr)) {
          newStyles = (newStyles ? newStyles + ' ' : '') + 'italic';
        }
      }

      switch (el.tagName.toLowerCase()) {
        case 'p': {
          const paragraph: PdfElement = { type: 'paragraph', children: [] };
          for (const child of Array.from(el.childNodes)) {
            processNode(child, paragraph.children, newStyles);
          }
          if (paragraph.children.length > 0) {
            container.push(paragraph);
          }
          break;
        }
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
          container.push({
            type: 'heading',
            level: parseInt(el.tagName.substring(1)),
            content: el.textContent?.trim() ?? ''
          });
          break;
        case 'strong':
        case 'b': {
          const combinedStyles = (newStyles ? newStyles + ' ' : '') + 'bold';
          for (const child of Array.from(el.childNodes)) {
            processNode(child, container, combinedStyles);
          }
          break;
        }
        case 'em':
        case 'i': {
          const combinedStyles = (newStyles ? newStyles + ' ' : '') + 'italic';
          for (const child of Array.from(el.childNodes)) {
            processNode(child, container, combinedStyles);
          }
          break;
        }
        case 'u':
        case 'ins': {
          const combinedStyles = (newStyles ? newStyles + ' ' : '') + 'underline';
          for (const child of Array.from(el.childNodes)) {
            processNode(child, container, combinedStyles);
          }
          break;
        }
        case 'strike':
        case 's':
        case 'del': {
          const combinedStyles = (newStyles ? newStyles + ' ' : '') + 'strikethrough';
          for (const child of Array.from(el.childNodes)) {
            processNode(child, container, combinedStyles);
          }
          break;
        }
        case 'span': {
          for (const child of Array.from(el.childNodes)) {
            processNode(child, container, newStyles);
          }
          break;
        }
        case 'ul':
        case 'ol': {
          const isOrdered = el.tagName.toLowerCase() === 'ol';
          const startAttr = el.getAttribute('start');
          const startNumber = startAttr ? parseInt(startAttr, 10) : 1;

          const list: PdfElement & { startNumber?: number } = {
            type: 'list',
            ordered: isOrdered,
            items: [],
            startNumber: isOrdered ? startNumber : undefined
          };

          let lastListItem: any = null;

          for (const child of Array.from(el.children)) {
            const childTag = (child as HTMLElement).tagName.toLowerCase();

            if (childTag === 'ul' || childTag === 'ol') {
              if (lastListItem) {
                const nestedList: PdfElement = {
                  type: 'list',
                  ordered: childTag === 'ol',
                  items: []
                };
                for (const nestedLi of Array.from((child as HTMLElement).children).filter(c => (c as HTMLElement).tagName.toLowerCase() === 'li')) {
                  const nestedItem: any = { content: [] };
                  for (const nestedChildNode of Array.from(nestedLi.childNodes)) {
                    processNode(nestedChildNode, nestedItem.content, newStyles);
                  }
                  nestedList.items.push(nestedItem);
                }
                lastListItem.nestedList = nestedList;
              }
              continue;
            }

            if (childTag !== 'li') continue;

            const li = child as HTMLElement;
            const listItem: any = { content: [] };
            lastListItem = listItem;

            for (const childNode of Array.from(li.childNodes)) {
              if (childNode.nodeType === Node.ELEMENT_NODE) {
                const childEl = childNode as HTMLElement;
                const tagName = childEl.tagName.toLowerCase();

                if (tagName === 'ul' || tagName === 'ol') {
                  const nestedList: PdfElement = {
                    type: 'list',
                    ordered: tagName === 'ol',
                    items: []
                  };
                  for (const nestedLi of Array.from(childEl.children).filter(child => child.tagName.toLowerCase() === 'li')) {
                    const nestedItem: any = { content: [] };
                    for (const nestedChild of Array.from(nestedLi.childNodes)) {
                      processNode(nestedChild, nestedItem.content, newStyles);
                    }
                    nestedList.items.push(nestedItem);
                  }
                  listItem.nestedList = nestedList;
                } else {
                  processNode(childNode, listItem.content, newStyles);
                }
              } else if (childNode.nodeType === Node.TEXT_NODE) {
                const text = childNode.textContent?.replace(/\s+/g, ' ').trim() ?? '';
                if (text && text !== '\u00a0') {
                  listItem.content.push({ type: 'text', content: text });
                }
              }
            }

            list.items.push(listItem);
          }
          if (list.items.length > 0) {
            container.push(list);
          }
          break;
        }
        case 'pre':
        case 'code':
          container.push({
            type: 'code',
            content: el.textContent?.trim() ?? '',
            language: el.getAttribute('data-language') || 'code'
          } as any);
          break;
        case 'blockquote':
          container.push({
            type: 'blockquote',
            content: el.textContent?.trim() ?? ''
          } as any);
          break;
        case 'img': {
          const src = el.getAttribute('src') || '';
          const cachedSrc = imageProcessor.getCached(src);
          const width = el.getAttribute('width');
          const height = el.getAttribute('height');
          container.push({
            type: 'image',
            src: cachedSrc || src,
            alt: el.getAttribute('alt') || '',
            width: width ?? undefined,
            height: height ?? undefined,
          });
          break;
        }
        case 'br':
          container.push({ type: 'break' });
          break;
        default:
          for (const child of Array.from(el.childNodes)) {
            processNode(child, container, newStyles);
          }
          break;
      }
    }
  };

  for (const child of Array.from(doc.body.childNodes)) {
    processNode(child);
  }
  return elements;
}

// Utility to strip HTML tags and preserve paragraph breaks
function htmlToParagraphs(html: string): string[] {
  if (!html) return [];
  // Replace <br> and <br/> with \n
  let withBreaks = html.replace(/<br\s*\/?>(?![^<]*<)/gi, '\n');
  // Replace <p> and <div> with \n (block-level)
  withBreaks = withBreaks.replace(/<\/?(p|div)[^>]*>/gi, '\n');
  // Remove all other tags
  const tmp = document.createElement('div');
  tmp.innerHTML = withBreaks;
  const text = tmp.textContent || tmp.innerText || '';
  // Split by double newlines or single newlines, filter empty
  return text.split(/\n+/).map(s => s.trim()).filter(Boolean);
}

// Utility to insert zero-width spaces in URLs for better wrapping
function makeUrlBreakable(url: string): string {
  if (!url) return '';
  // Add zero-width space after common URL delimiters to allow breaking
  return url
    .replace(/\//g, '/\u200B')  // After slashes
    .replace(/\?/g, '?\u200B')  // After question marks
    .replace(/&/g, '&\u200B')   // After ampersands
    .replace(/=/g, '=\u200B')   // After equals
    .replace(/-/g, '-\u200B')   // After hyphens
    .replace(/_/g, '_\u200B')   // After underscores
    .replace(/\./g, '.\u200B') // After dots
    .replace(/,/g, ',\u200B');  // After commas
}

// Format the target URL for the Scope page, specifically handling the
// "Asset > Target > endpoints" format by extracting just the Target and endpoint count
function formatTargetUrlForScope(url: string): string {
  if (!url) return '';
  const parts = url.split(' > ').map(p => p.trim());
  if (parts.length >= 3) {
    // Format is likely: Asset > Target > endpoint1, endpoint2
    const target = parts[1];
    // Re-join the rest in case there are > symbols in the endpoints 
    const endpointsStr = parts.slice(2).join(' > ');
    const endpointsList = endpointsStr.split(',').map(e => e.trim()).filter(Boolean);
    const count = endpointsList.length;
    return count > 0 ? `${target} (${count} endpoint${count > 1 ? 's' : ''})` : target;
  } else if (parts.length === 2) {
    // Format is likely: Asset > Target
    return parts[1];
  }
  // Legacy format or plain URL
  return url;
}

// For general note paragraphs, avoid inserting breakpoints after '/'
function makeNoteBreakable(text: string): string {
  if (!text) return '';
  return text
    .replace(/,/g, ',\u200B')   // After commas
    .replace(/:/g, ':\u200B')   // After colons
    .replace(/-/g, '-\u200B')   // After hyphens
    .replace(/_/g, '_\u200B')   // After underscores
    .replace(/\./g, '.\u200B'); // After dots
}

const ScopePage: React.FC<ScopePageProps> = ({ reportData, sectionId }) => {
  const { updateSectionPage } = useSectionPages();
  return (
    <Page size="A4" id={sectionId} style={{
      flexDirection: 'column',
      backgroundColor: '#ffffff',
      padding: '20mm 15mm',
      fontFamily: 'Roboto',
      fontSize: 12,
    }}>
      <View style={{ paddingHorizontal: 14, paddingTop: 1, flexDirection: 'column', flex: 1 }}>
        <View style={{ flex: 1 }}>
          <Text style={{ fontSize: 16, fontWeight: 'bold', color: '#2563eb', marginBottom: 12, lineHeight: 1.4 }}>
            SCOPE
          </Text>
          {reportData.scope ? (
            <View style={{ marginBottom: 14 }}>
              {renderPdfElements(ensurePdfElements(reportData.scope))}
            </View>
          ) : (
            <Text style={{ fontSize: 12, lineHeight: 1.4, marginBottom: 18, color: '#374151', textAlign: 'justify' }}>
              The scope of the assessment was limited to performing Vulnerability Assessment and Penetration Testing on the  {reportData.program_name} mentioned below:
            </Text>
          )}
          <View style={{
            borderRadius: 6,
            borderWidth: 1,
            borderColor: '#cbd5e1',
            marginBottom: 10,
            width: '100%',
            alignSelf: 'center',
            backgroundColor: '#fff',
            overflow: 'hidden',
          }}>
            <View style={{ flexDirection: 'column', width: '100%' }}>
              {/* Table Header */}
              <View style={{
                flexDirection: 'row',
                backgroundColor: '#1e40af',
                borderBottomWidth: 2,
                borderBottomColor: '#1e3a8a'
              }}>
                <Text style={{
                  paddingVertical: 8,
                  paddingHorizontal: 10,
                  textAlign: 'center',
                  fontWeight: 'bold',
                  width: '12%',
                  color: '#fff',
                  fontSize: 10,
                  lineHeight: 1.3
                }}>No.</Text>
                <Text style={{
                  paddingVertical: 8,
                  paddingHorizontal: 10,
                  textAlign: 'left',
                  fontWeight: 'bold',
                  width: '25%',
                  color: '#fff',
                  fontSize: 10,
                  lineHeight: 1.3
                }}>Type</Text>
                <Text style={{
                  paddingVertical: 8,
                  paddingHorizontal: 10,
                  textAlign: 'left',
                  fontWeight: 'bold',
                  width: '63%',
                  color: '#fff',
                  fontSize: 10,
                  lineHeight: 1.3
                }}>Target URL / Asset</Text>
              </View>
              {/* Table Rows */}
              {reportData.target_details && reportData.target_details.map((target, idx) => (
                <View key={idx} style={{
                  flexDirection: 'row',
                  backgroundColor: idx % 2 === 0 ? '#f8fafc' : '#fff',
                  borderBottomWidth: idx === reportData.target_details.length - 1 ? 0 : 1,
                  borderBottomColor: '#e2e8f0',
                  minHeight: 28,
                  alignItems: 'center'
                }}>
                  <Text style={{
                    paddingVertical: 6,
                    paddingHorizontal: 10,
                    width: '12%',
                    textAlign: 'center',
                    fontSize: 10,
                    color: '#475569',
                    fontWeight: 'bold',
                    lineHeight: 1.3
                  }}>{idx + 1}</Text>
                  <Text style={{
                    paddingVertical: 6,
                    paddingHorizontal: 10,
                    width: '25%',
                    fontSize: 10,
                    color: '#334155',
                    lineHeight: 1.3
                  }}>{target.target_type || target.type}</Text>
                  <View style={{
                    paddingVertical: 6,
                    paddingHorizontal: 10,
                    width: '63%'
                  }}>
                    <Text style={{
                      fontSize: 9,
                      lineHeight: 1.3,
                      color: '#0f172a'
                    }}>
                      {makeUrlBreakable(
                        target.endpoints && Array.isArray(target.endpoints)
                          ? (target.endpoints.length > 0
                              ? `${target.target_value || target.target_name || ''} (${target.endpoints.length} endpoint${target.endpoints.length > 1 ? 's' : ''})`
                              : `${target.target_value || target.target_name || ''}`)
                          : formatTargetUrlForScope(target.url || target.target_value || target.target_name || '')
                      )}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
          <Text style={{ fontSize: 10, color: '#64748b', textAlign: 'center', marginTop: 10, fontStyle: 'italic', lineHeight: 1.3 }}>Table 1: Scope of Work</Text>
          {reportData.scope_notes && (
            <View style={{ marginTop: 16 }}>
              {renderPdfElements(ensurePdfElements(reportData.scope_notes))}
            </View>
          )}

        </View>
      </View>
      <View style={styles.footer} fixed>
        <Text style={styles.footerLeft}>{reportData.document_number || 'Document Number'}</Text>
        <Text style={styles.footerRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
      </View>
      <Text style={{ display: 'none' }} render={({ pageNumber }) => { updateSectionPage('Scope', pageNumber); return ''; }} fixed />
    </Page>
  );
};

export default ScopePage;